import { useState } from "react"
import { produce } from "immer"
import { HxMapGroup } from "../../../../Comps/HxMap/HxMapGroup"
import { NotesSelectMenu } from "../../../../Comps/NoteButtons/NotesSelect"
import { HexMap } from "../../../../Data/HexMap/HexMap"
import { HexMapBuilderParams, BuildHexMap } from "../../../../Data/HexMap/HexMapBuilder"
import { HexMapModifier } from "../../../../Data/HexMap/HexMapModifier"
import { Note } from "../../../../Data/HexMap/Note"
import { FlexRowTwo } from "../../../../Layouts/FlexRows"
import { useNoteNames } from "../../../../Project/ProjectStore"
import { All } from "../../../../Util/Constants/Quantities"
import { Music, useScaleNotes } from "../../../../Util/Music/Music"
import { TransformTranslate } from "../../../../Util/Transform/Transform"
import { IconEye } from "../../../../Comps/Icons/IconEye"
import { CommonNotes } from "../CommonNotes"

export function CycleOfThirdsPair(){ 
    const [tonic, setTonic] = useState(Music.G)
    const getAdjustedTonic = (t:number) => { return t }
    const getMap = () => {
        const params:HexMapBuilderParams = {rows:2, columns:2, tonic:getAdjustedTonic(tonic), radius:80}
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        mod.keys_setOpen([0,2], true)
        mod.keys_setOpen([1,3], false)
        mod.keys_setVisible([1,3], false)
        //mod.notes_setPrimaryLabelAsNoteIndex([All])
        return map
    }
    const [map, setMap] = useState<HexMap>(getMap())
    const [width,height] = [400,400]
    const onNoteClick = (note:Note) => {
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_advanceSelectedState([note.params.noteIndex])
        }))
    }
    const resetTonic = (t:number) => {
        setTonic(t)
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.setTonic(getAdjustedTonic(t))
        }))
    }
    const commonNoteIndexes:number[] = [2,5,6,13]
    const showCommonNotes = () => {
        const noteIndexes:number[] = commonNoteIndexes
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setSelected(noteIndexes)
        }))
    }
    const showCommonChords = () => {
        const noteIndexes:number[] = []
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setHilited(noteIndexes)
        }))
    }
    const showMinorChain = () => {
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setHilited([1,2,10,12])
        }))
    }
    const showMajorChain = () => {
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setHilited([3,4,5,13])
        }))
    }
    const showSus2 = () => {
        const noteIndexes:number[] = [2,5,6,13]
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setHilited(noteIndexes)
        }))
    }
    const showNotes = (noteIndexes:number[]) => {
        setMap(produce((draft:HexMap)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_setDefault([All])
            mod.notes_setHilited(noteIndexes)
        }))
    }
    const noteNames:string[] = useNoteNames()
    const leftTonicNote:string = noteNames[tonic]
    const rightTonicNote:string = noteNames[(tonic + 4) % 12]
    const scale:string[] = useScaleNotes(tonic)
    const bottomScale:string[] = useScaleNotes((tonic + 4) % 12)
    const clear = () => {
        setMap(getMap())
    }
    return(
        <div className={'harmonic-cycle-pair'}>
            <FlexRowTwo columnOneFlex={1} columnTwoFlex={1}>
               <div className={'centered'} style={{marginTop:30}}> 
                    <div>
                        <NotesSelectMenu onChange={resetTonic} tonic={tonic}/>
                    </div>
                    <svg width={width} height={height} className={'pair-hexmap'}>
                        <g transform={TransformTranslate(width/2, height/2)}>
                            <HxMapGroup hexMap={map} onNoteClick={onNoteClick}/>
                        </g>
                    </svg>
                    <div><button style={{marginTop:25}} type='button' onClick={clear}>Clear</button></div>
                </div>
                <div className={'pair-description'}>
                    <h2>Properties of Two Keys Along a Cycle of Flat Thirds</h2>
                    <ul>
                        <li>Common Notes: 3 of 7 <CommonNotes map={map} noteIndexes={commonNoteIndexes}/> <IconEye onClick={showCommonNotes}/></li>
                        <li>Common Triadic Chords: 0 of 7<IconEye onClick={showCommonChords}/></li>
                        <li>Common Sus Chords: {scale[5]} Sus2 & {scale[2]} Sus4 <IconEye onClick={showSus2}/></li>
                        <li>The bottom right key ({rightTonicNote}) is a third (4 notes) higher than the top left key ({leftTonicNote}).</li>
                        <li>Conversely, the top left key ({leftTonicNote}) is a flat sixth (8 notes) higher than the bottom right key ({rightTonicNote})</li>   
                    </ul>
                    <h2>Common Modulation Techniques:</h2>
                    <ul>
                        <li>Chain of Minor Chords: {scale[1]}, {scale[2]}, {bottomScale[1]}, {bottomScale[2]} <IconEye onClick={showMinorChain}/></li>
                        <li>Chain of Major Chords: {scale[3]}, {scale[4]}, {bottomScale[3]}, {bottomScale[4]} <IconEye onClick={showMajorChain}/></li>
                    </ul>
                    <h2>Parallel Modes (top left key to bottom right key):</h2>
                    <ul>
                        <li>
                            {scale[2]} Phrygian in {leftTonicNote} and {scale[2]} Ionian (major) in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([2])}}/>
                        </li>
                        <li>
                            {scale[5]} Aeolian (minor) in {leftTonicNote} and {scale[5]} Lydian in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([5])}}/>
                        </li>
                        <li>
                            {scale[6]} Locrian in {leftTonicNote} and {scale[6]} Mixolydian in {rightTonicNote} 
                            <IconEye onClick={()=>{showNotes([6,13])}}/>

                        </li>
                    </ul>
                </div>
            </FlexRowTwo>

        </div>
    )
}